import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Lightbulb, Lamp, Cable, Sun, Factory, Zap, Sparkles, Home, Shield } from "lucide-react";

export default function LighteningSolutions() {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  const features = [
    { icon: Zap, key: "smartLighting", color: "#ffde21" },
    { icon: Lamp, key: "decorativeLighting", color: "#ffde21" },
    { icon: Cable, key: "ledStrips", color: "#ffde21" },
    { icon: Sun, key: "outdoorLighting", color: "#ffde21" },
    { icon: Factory, key: "industrialLighting", color: "#ffde21" },
    { icon: Lightbulb, key: "bulbs", color: "#ffde21" }
  ];

  const lightingTypes = [
    {
      title: dir === "rtl" ? "إنارة داخلية" : "Indoor Lighting",
      desc: dir === "rtl" ? "إضاءة مثالية لكل غرفة" : "Perfect lighting for every room",
      icon: Home,
      gradient: "linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)"
    },
    {
      title: dir === "rtl" ? "إنارة خارجية" : "Outdoor Lighting",
      desc: dir === "rtl" ? "إضاءة آمنة وجميلة للحدائق" : "Safe and beautiful garden lighting",
      icon: Sun,
      gradient: "linear-gradient(135deg, #ff8c00 0%, #ffa500 100%)"
    },
    {
      title: dir === "rtl" ? "إنارة ذكية" : "Smart Lighting",
      desc: dir === "rtl" ? "تحكم كامل من هاتفك" : "Full control from your phone",
      icon: Zap,
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    }
  ];

  return (
    <div dir={dir} className="min-h-screen" style={{ background: '#fafcf0' }}>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #2d2d2b 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
            {/* Image Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
                  style={{ background: '#ffde21' }}
                ></div>
                <img
                  src="/images/lightingsolutionsimg.webp"
                  alt={t("lightingSolutions")}
                  className="relative rounded-3xl shadow-2xl w-full object-cover"
                  style={{ border: '3px solid #ffde21' }}
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
            </motion.div>

            {/* Text Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="inline-block mb-4">
                <span
                  className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
                  style={{
                    background: 'rgba(255, 222, 33, 0.2)',
                    color: '#2d2d2b'
                  }}
                >
                  <Sparkles size={16} />
                  {dir === "rtl" ? "إضاءة مبتكرة" : "Innovative Lighting"}
                </span>
              </div>

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight"
                style={{ color: '#2d2d2b' }}
              >
                {t("lightingSolutions")}
              </h1>

              <p
                className="text-lg md:text-xl leading-relaxed mb-8"
                style={{ color: '#2d2d2b', opacity: 0.8 }}
              >
                {dir === "rtl"
                  ? "حلول إنارة شاملة تجمع بين الجمال والكفاءة، من الإضاءة الذكية إلى الحلول الصناعية المتقدمة"
                  : "Comprehensive lighting solutions combining beauty and efficiency, from smart lighting to advanced industrial solutions"}
              </p>

              <button
                className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                style={{
                  background: '#ffde21',
                  color: '#2d2d2b'
                }}
              >
                {dir === "rtl" ? "احصل على استشارة" : "Get Consultation"}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "مجموعة حلول الإنارة" : "Lighting Solutions Range"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "خيارات متنوعة تناسب جميع احتياجاتك من الإنارة"
                : "Diverse options to suit all your lighting needs"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group cursor-pointer"
                  style={{
                    background: 'white',
                    border: '2px solid rgba(255, 222, 33, 0.2)'
                  }}
                >
                  {/* Icon Circle */}
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>

                  {/* Feature Title */}
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: '#2d2d2b' }}
                  >
                    {t(`lightingSolutions_features.${feature.key}`)}
                  </h3>

                  {/* Decorative Element */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transition-all duration-300 group-hover:h-2"
                    style={{ background: '#ffde21' }}
                  ></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lighting Types Section */}
      <section
        className="py-16 md:py-24 relative overflow-hidden"
        style={{ background: '#2d2d2b' }}
      >
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21),
                             linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21)`,
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#ffde21' }}
            >
              {dir === "rtl" ? "أنواع الإنارة" : "Lighting Categories"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: 'white', opacity: 0.9 }}
            >
              {dir === "rtl"
                ? "حلول متخصصة لكل نوع من أنواع الإضاءة"
                : "Specialized solutions for every type of lighting"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {lightingTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative p-8 rounded-2xl text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group"
                  style={{
                    background: 'rgba(250, 252, 240, 0.05)',
                    border: '2px solid rgba(255, 222, 33, 0.3)'
                  }}
                >
                  {/* Gradient Circle */}
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 transition-transform duration-300 group-hover:scale-110"
                    style={{ background: type.gradient }}
                  >
                    <Icon size={40} style={{ color: 'white' }} strokeWidth={2.5} />
                  </div>

                  <h3
                    className="text-xl font-bold mb-3"
                    style={{ color: 'white' }}
                  >
                    {type.title}
                  </h3>
                  <p style={{ color: 'white', opacity: 0.8 }}>
                    {type.desc}
                  </p>

                  {/* Corner Accent */}
                  <div
                    className="absolute top-4 right-4 w-2 h-2 rounded-full"
                    style={{ background: '#ffde21', opacity: 0.5 }}
                  ></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Smart Lighting Features */}
      <section className="py-16 md:py-24" style={{ background: 'rgba(255, 222, 33, 0.05)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "مميزات الإنارة الذكية" : "Smart Lighting Features"}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl"
              style={{
                background: 'white',
                border: '2px solid rgba(255, 222, 33, 0.3)'
              }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  border: '2px solid #ffde21'
                }}
              >
                <Zap size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: '#2d2d2b' }}
              >
                {dir === "rtl" ? "كفاءة في استهلاك الطاقة" : "Energy Efficiency"}
              </h3>
              <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                {dir === "rtl"
                  ? "توفير يصل إلى 80% من استهلاك الكهرباء مع تقنية LED الحديثة"
                  : "Save up to 80% on electricity consumption with modern LED technology"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl"
              style={{
                background: 'white',
                border: '2px solid rgba(255, 222, 33, 0.3)'
              }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  border: '2px solid #ffde21'
                }}
              >
                <Sparkles size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: '#2d2d2b' }}
              >
                {dir === "rtl" ? "تحكم ذكي" : "Smart Control"}
              </h3>
              <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                {dir === "rtl"
                  ? "تحكم في الإضاءة من أي مكان عبر تطبيق الهاتف أو الأوامر الصوتية"
                  : "Control lighting from anywhere via mobile app or voice commands"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl"
              style={{
                background: 'white',
                border: '2px solid rgba(255, 222, 33, 0.3)'
              }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  border: '2px solid #ffde21'
                }}
              >
                <Lamp size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: '#2d2d2b' }}
              >
                {dir === "rtl" ? "تصاميم متنوعة" : "Diverse Designs"}
              </h3>
              <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                {dir === "rtl"
                  ? "مجموعة واسعة من التصاميم العصرية والكلاسيكية"
                  : "Wide range of modern and classic designs"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl"
              style={{
                background: 'white',
                border: '2px solid rgba(255, 222, 33, 0.3)'
              }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  border: '2px solid #ffde21'
                }}
              >
                <Shield size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: '#2d2d2b' }}
              >
                {dir === "rtl" ? "جودة عالية" : "High Quality"}
              </h3>
              <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                {dir === "rtl"
                  ? "منتجات من أفضل العلامات التجارية مع ضمان طويل الأمد"
                  : "Products from top brands with long-term warranty"}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Lightbulb size={60} style={{ color: '#ffde21', margin: '0 auto', marginBottom: '1.5rem' }} />
            <h2
              className="text-3xl md:text-4xl font-black mb-6"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "جاهز لتحويل إضاءتك؟" : "Ready to Transform Your Lighting?"}
            </h2>
            <p
              className="text-lg md:text-xl mb-8"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "احصل على استشارة مجانية من خبرائنا لاختيار الحل الأمثل لمساحتك"
                : "Get a free consultation from our experts to choose the optimal solution for your space"}
            </p>
            <Link to="/contact">
              <button
                className="px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                style={{
                  background: '#ffde21',
                  color: '#2d2d2b'
                }}
              >
                {t("homepage.cta.button")}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}